import React, { useState, useEffect } from 'react';
import VisibilityBadges from './VisibilityBadges';

export default function ProductInsights({ locationId, locationName }) {
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);

  // Sorting
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Filters
  const [filterOptions, setFilterOptions] = useState({ categories: [], menus: [] });
  const [activeFilters, setActiveFilters] = useState({
    search: '',
    minPrice: '',
    maxPrice: '',
    category: '',
    menu: ''
  });

  // Stats
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (locationId) {
      fetchProducts();
    }
  }, [page, limit, sortBy, sortOrder, activeFilters, locationId]);

  async function fetchProducts() {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page,
        limit,
        sortBy,
        sortOrder,
        locationId
      });

      if (activeFilters.search) params.append('search', activeFilters.search);
      if (activeFilters.minPrice) params.append('minPrice', activeFilters.minPrice);
      if (activeFilters.maxPrice) params.append('maxPrice', activeFilters.maxPrice);
      if (activeFilters.category) params.append('category', activeFilters.category);
      if (activeFilters.menu) params.append('menu', activeFilters.menu);

      const response = await fetch(`/api/insights/products?${params}`);
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();

      setProducts(data.products);
      setPage(data.pagination.page);
      setTotal(data.pagination.total);
      setPages(data.pagination.pages);
      setStats(data.stats);
      setFilterOptions(data.filters);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleFilterChange(filterName, value) {
    setActiveFilters(prev => ({ ...prev, [filterName]: value }));
    setPage(1);
  }

  function handleSort(column) {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  }

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <span className="sort-icon">⇅</span>;
    return sortOrder === 'asc' ? <span className="sort-icon">↑</span> : <span className="sort-icon">↓</span>;
  };

  return (
    <div className="insights-container">
      <h2>📊 Product Insights {locationName && `- ${locationName}`}</h2>
      {!locationId && <div className="info-message">📍 Please select a location first</div>}

      {/* Stats Panel */}
      {stats && (
        <div className="stats-panel">
          <h3>📊 Summary Statistics</h3>
          <div className="stats-grid">
            <StatCard
              label="Total Products"
              value={stats.total_products}
              icon="📦"
            />
            <StatCard
              label="Products with Price"
              value={stats.products_with_price}
              icon="💰"
            />
            <StatCard
              label="Avg Price"
              value={`$${(stats.avg_price || 0).toFixed(2)}`}
              icon="📈"
            />
            <StatCard
              label="Price Range"
              value={`$${(stats.min_price || 0).toFixed(2)} - $${(stats.max_price || 0).toFixed(2)}`}
              icon="📊"
            />
            <StatCard
              label="Categories"
              value={stats.category_count}
              icon="🏷️"
            />
            <StatCard
              label="Total Records"
              value={total}
              icon="📋"
            />
          </div>
        </div>
      )}

      {/* Filters Panel */}
      <div className="filters-panel">
        <h3>🔍 Filters</h3>
        <div className="filters-grid">
          <div className="filter-item">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search products..."
              value={activeFilters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-item">
            <label>Min Price</label>
            <input
              type="number"
              placeholder="0.00"
              value={activeFilters.minPrice}
              onChange={(e) => handleFilterChange('minPrice', e.target.value)}
              className="filter-input"
              step="0.01"
            />
          </div>

          <div className="filter-item">
            <label>Max Price</label>
            <input
              type="number"
              placeholder="999.99"
              value={activeFilters.maxPrice}
              onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
              className="filter-input"
              step="0.01"
            />
          </div>

          <div className="filter-item">
            <label>Category</label>
            <select
              value={activeFilters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="filter-input"
            >
              <option value="">All Categories</option>
              {filterOptions.categories?.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Menu</label>
            <select
              value={activeFilters.menu}
              onChange={(e) => handleFilterChange('menu', e.target.value)}
              className="filter-input"
            >
              <option value="">All Menus</option>
              {filterOptions.menus?.map(menu => (
                <option key={menu.guid} value={menu.guid}>{menu.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Per Page</label>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setPage(1);
              }}
              className="filter-input"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="table-section">
        <h3>📋 Products Table</h3>

        {loading && <p className="loading">Loading...</p>}
        {error && <div className="error">✕ {error}</div>}

        {!loading && !error && (
          <>
            <div className="table-wrapper">
              <table className="insights-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')}>
                      Product Name <SortIcon column="name" />
                    </th>
                    <th onClick={() => handleSort('price')}>
                      Price <SortIcon column="price" />
                    </th>
                    <th>Base Price</th>
                    <th onClick={() => handleSort('pricing_strategy')}>
                      Strategy <SortIcon column="pricing_strategy" />
                    </th>
                    <th onClick={() => handleSort('sales_category_name')}>
                      Category <SortIcon column="sales_category_name" />
                    </th>
                    <th>Menu</th>
                    <th>Group</th>
                    <th>Visibility</th>
                    <th>Taxes</th>
                    <th>Allergens</th>
                    <th>Images</th>
                    <th>Pricing Rules</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => (
                    <tr key={product.guid}>
                      <td className="name-cell">{product.name}</td>
                      <td className="price-cell">${(product.price || 0).toFixed(2)}</td>
                      <td>${(product.base_price || 0).toFixed(2)}</td>
                      <td className="strategy-cell">{product.pricing_strategy || 'N/A'}</td>
                      <td>{product.sales_category_name || 'N/A'}</td>
                      <td>{product.menu_name || '-'}</td>
                      <td>{product.menu_group_name || '-'}</td>
                      <td><VisibilityBadges visibility={product.visibility} /></td>
                      <td><TaxBadges taxDetails={product.tax_details} /></td>
                      <td className="badge">{product.allergen_count || 0}</td>
                      <td className="badge">{product.image_count || 0}</td>
                      <td className="badge">{product.pricing_rule_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {products.length === 0 && <p className="empty-message">No products found</p>}

            {/* Pagination */}
            <div className="pagination">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="pagination-btn"
              >
                ⏮️ First
              </button>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="pagination-btn"
              >
                ⬅️ Previous
              </button>

              <span className="pagination-info">
                Page <strong>{page}</strong> of <strong>{pages}</strong> | Total: <strong>{total}</strong> products
              </span>

              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pages}
                className="pagination-btn"
              >
                Next ➡️
              </button>
              <button
                onClick={() => setPage(pages)}
                disabled={page === pages}
                className="pagination-btn"
              >
                Last ⏭️
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="stat-card">
      <div className="stat-icon">{icon}</div>
      <div className="stat-content">
        <p className="stat-label">{label}</p>
        <p className="stat-value">{value}</p>
      </div>
    </div>
  );
}

// tax_details comes from the API as "Sales Tax:9,City Admission/Food & Bev Tax:1"
// (GROUP_CONCAT of "name:percentage" pairs) so each product's actual tax
// rates are visible directly in the table, not just a count.
function TaxBadges({ taxDetails }) {
  if (!taxDetails) return <span className="visibility-empty">-</span>;

  const entries = taxDetails.split(',').map(pair => {
    const separatorIndex = pair.lastIndexOf(':');
    const name = separatorIndex === -1 ? pair : pair.substring(0, separatorIndex);
    const percentage = separatorIndex === -1 ? '' : pair.substring(separatorIndex + 1);
    return { name, percentage };
  }).filter(entry => entry.name);

  if (entries.length === 0) return <span className="visibility-empty">-</span>;

  return (
    <div className="tax-badges">
      {entries.map((entry, idx) => (
        <span key={idx} className="tax-badge" title={entry.name}>
          {entry.name}{entry.percentage !== '' ? ` ${entry.percentage}%` : ''}
        </span>
      ))}
    </div>
  );
}
